import type { UserEditableFormControlElement, ValidatorFn } from "../utils/types";
import { VisibleFieldMap } from "./visibleFieldMap";
import * as validatorRules from "../rules/builtin";

export class FieldValidation {
    constructor(
        private customValidators: Record<string, ValidatorFn>,
        private visibleFieldMap: VisibleFieldMap
    ) { }

    validateField(field: UserEditableFormControlElement, form: HTMLFormElement): boolean {
        let message = "";

        // Required
        if (field.hasAttribute('data-val-required')) {
            if (this.customValidators["required"]) {
                message = this.validateCustomRule("required", field, form);
            }

            if (!message) {
                message = validatorRules.validateRequired(field);
            }
        }

        // Email
        if (!message && field.hasAttribute('data-val-email')) {
            if (this.customValidators["email"]) {
                message = this.validateCustomRule("email", field, form);
            }

            if (!message) {
                message = validatorRules.validateEmail(field);
            }
        }

        // StringLength
        if (!message && field.hasAttribute('data-val-length')) {
            if (this.customValidators["length"]) {
                message = this.validateCustomRule("length", field, form);
            }

            if (!message) {
                message = validatorRules.validateLength(field);
            }
        }

        // MinLength
        if (!message && field.hasAttribute('data-val-minlength')) {
            if (this.customValidators["minlength"]) {
                message = this.validateCustomRule("minlength", field, form);
            }
            
            if (!message) {
                message = validatorRules.validateMinLength(field);
            }
        }

        // MaxLength
        if (!message && field.hasAttribute('data-val-maxlength')) {
            if (this.customValidators["maxlength"]) {
                message = this.validateCustomRule("maxlength", field, form);
            }

            if (!message) {
                message = validatorRules.validateMaxLength(field);
            }
        }

        // Regex
        if (!message && field.hasAttribute('data-val-regex')) {
            if (this.customValidators["regex"]) {
                message = this.validateCustomRule("regex", field, form);
            }

            if (!message) {
                message = validatorRules.validateRegex(field);
            }
        }

        // Range
        if (!message && field.hasAttribute('data-val-range')) {
            if (this.customValidators["range"]) {
                message = this.validateCustomRule("range", field, form);
            }

            if (!message) {
                message = validatorRules.validateRange(field);
            }
        }

        // Compare
        if (!message && field.hasAttribute('data-val-equalto')) {
            if (this.customValidators["equalto"]) {
                message = this.validateCustomRule("equalto", field, form);
            }

            if (!message) {
                message = validatorRules.validateCompare(field, form);
            }
        }

        // CUSTOM RULES ON NON HANDLED RULE NAMES
        for (const ruleName in this.customValidators) {
            if (!message && field.hasAttribute(`data-val-${ruleName}`)) {
                message = this.validateCustomRule(ruleName, field, form);
            }
        }

        this.setupErrorFeedback(field, form, message);

        return !message;
    }

    clearFormValidation(form: HTMLFormElement): void {
        form.querySelectorAll<HTMLElement>(`[data-valmsg-for]`).forEach((msgSpan) => {
            msgSpan.textContent = '';
            msgSpan.classList.remove('field-validation-error');
            msgSpan.classList.add('field-validation-valid');
        });

        form.querySelectorAll<UserEditableFormControlElement>(`input[data-val="true"],select[data-val="true"],textArea[data-val="true"]`).forEach((field) => {
            const visibleField = this.visibleFieldMap.get(field);
            visibleField.classList.remove('is-invalid');
        });

        form.querySelector<HTMLElement>('.validation-summary-errors')?.remove();
    }

    // === Custom rule validation helper ===
    private validateCustomRule(ruleName: string, field: UserEditableFormControlElement, form: HTMLFormElement): string {
        let message = "";

        const visibleField = this.visibleFieldMap.get(field);
        const validatorFn = this.customValidators[ruleName];

        if (typeof validatorFn !== "function") return "";

        const result = validatorFn(field, form, visibleField);

        if (typeof result === "string" && result.trim()) {
            message = result;
        }

        return message;
    }

    // === Error feedback ===
    private setupErrorFeedback(field: UserEditableFormControlElement, form: HTMLFormElement, message: string | null): void {
        const fieldName = field.getAttribute('name') || "";
        const msgSpan = form.querySelector<HTMLElement>(`[data-valmsg-for="${fieldName}"]`);
        if (!msgSpan) return;

        const visibleField = this.visibleFieldMap.get(field);

        if (message) {
            msgSpan.textContent = message;
            msgSpan.classList.replace('field-validation-valid', 'field-validation-error');
            visibleField.classList.add('is-invalid');
        } else {
            msgSpan.textContent = '';
            msgSpan.classList.replace('field-validation-error', 'field-validation-valid');
            visibleField.classList.remove('is-invalid');
        }
    }
}
